import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userRoleAssignmentApi } from '../api/userRoleAssignmentApi';
import type { CreateUserRoleAssignmentInput } from './types';

export const userRoleAssignmentKeys = {
  all: ['userRoleAssignment'] as const,
  list: (userObjectId?: string) =>
    [...userRoleAssignmentKeys.all, 'list', userObjectId ?? 'all'] as const,
};

/**
 * List assignments — all users if no `userObjectId`, or scoped to a single user.
 * Meant to be called from the Vera Admin UI, gated by the global `Admin` role.
 */
export function useUserRoleAssignments(userObjectId?: string) {
  return useQuery({
    queryKey: userRoleAssignmentKeys.list(userObjectId),
    queryFn: () => userRoleAssignmentApi.list(userObjectId),
    staleTime: 30 * 1000,
  });
}

export function useGrantRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserRoleAssignmentInput) =>
      userRoleAssignmentApi.grant(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userRoleAssignmentKeys.all });
    },
  });
}

export function useRevokeRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { userObjectId: string; roleValue: string }) =>
      userRoleAssignmentApi.revokeByUserAndRole(params.userObjectId, params.roleValue),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userRoleAssignmentKeys.all });
    },
  });
}
